import { Api, DisplayType, Keys, ModelRow, Navigation, UrlUtil } from "@mcleod/core";
import {
    Component, ContextMenuItemCreator, ContextMenuLabelProps, DataSourceAction, DataSourceExecutionEvent, Image,
    KeyEvent, Label, Snackbar, TableRow, TableRowDisplayEvent, TableRowExpansionEvent, TableSelectionEvent, Toast
} from "@mcleod/components";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { AutogenLayoutTransmissionControlList } from "./autogen/AutogenLayoutTransmissionControlList";
import { EdiUtility } from "./EdiUtility";
import { TransmissionControlExpanded } from "./TransmissionControlExpanded";

export class TransmissionControlList extends AutogenLayoutTransmissionControlList {
    private _expanded: boolean = false;

    override async onLoad() {
        this.switchIntercompany.allowIndeterminate = true;
        this.switchIntercompany.checked = undefined;
        this.textboxCompany.visible = false;
        this.buttonRetransmit.enabled = false;
        this.buttonBypassBatch.enabled = false;
        this.buttonAddRecovery.enabled = false;
        this.buttonViewRecovery.enabled = false;
        this.textboxTextSearch.visible = false;
        const urlProp = UrlUtil.getPropsFromUrl(window.location.search);
        if (urlProp !== null) {
            if (urlProp.id !== null && urlProp.id !== undefined) {
                await this.mainDataSource.search({ "edi_log.id": urlProp.id });
            } else if (urlProp.ids !== null && urlProp.ids !== undefined) {
                await this.mainDataSource.search({ ids: urlProp.ids });
            }
        }
        this.textboxStatus.requiredDuringSearch = false;
    }

    tableTransmissionsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = event.getTableRow().data as ModelRow;
        const labelWasSuccessful = tableRow.findComponentById("labelWasSuccessful") as Label;
        const labelFilename = tableRow.findComponentById("labelFilename") as Label;
        const labelPartnerName = tableRow.findComponentById("labelPartnerName") as Label;
        const intercompanyImage = tableRow.findComponentById("imageIntercompany") as Image;
        const partnerCompany = modelRow.get("edi_log.partner_company");
        intercompanyImage.visible = (partnerCompany !== null && partnerCompany !== undefined);
        tableRow.contextMenuItems = () => this.getContextMenuItems();
        this.setWasSuccessfulColor(labelWasSuccessful);
        this.setTooltipContent(labelFilename, modelRow);
        this.setTooltipContent(labelPartnerName, modelRow);
        const urlProp = UrlUtil.getPropsFromUrl(window.location.search);
        if (urlProp !== null && !this.expanded) {
            if (urlProp.id !== null && urlProp.id !== undefined) {
                tableRow.expanderButton.clicked();
                this.expanded = true;
            }
        }
    }

    private setWasSuccessfulColor(label: Label) {
        switch (label.text) {
            case "Successful":
            case "Recovered":
                label.color = "success";
                break;
            case "Failed":
                label.color = "error";
                break;
            default:
                label.color = "default";
                break;
        }
    }

    private setTooltipContent(component: Component, modelRow: ModelRow) {
        if (component.id === null) return;
        switch (component.id) {
            case "labelFilename":
                return CommonTooltips.setTooltipContentTextboxes(component, () => [
                    { caption: "Filename", displayType: DisplayType.STRING, text: modelRow.get("edi_interchange.filename") },
                    { caption: "Internal Filename", displayType: DisplayType.STRING, text: modelRow.get("edi_log.internal_filename") },
                    { caption: "Log Name", displayType: DisplayType.STRING, text: modelRow.get("edi_log.message_id") },
                    { caption: "Interchange ID", displayType: DisplayType.STRING, text: modelRow.get("edi_interchange.id") },
                    { caption: "Transmission ID", displayType: DisplayType.STRING, text: modelRow.get("edi_log.id") },
                    { caption: "Total Functional Groups", displayType: DisplayType.STRING, text: modelRow.get("edi_log.total_func_grps") },
                ]);
            case "labelPartnerName":
                return EdiUtility.getPartnerNameTooltipForTransmission(component, modelRow, false);
        }
    }

    tableTransmissionsOnSelect(event: TableSelectionEvent) {
        const someRowsSelected = event.table.selectedRowCount > 0;
        this.buttonRetransmit.enabled = someRowsSelected;
        this.buttonBypassBatch.enabled = someRowsSelected;
        this.buttonAddRecovery.enabled = someRowsSelected;
        this.buttonViewRecovery.enabled = someRowsSelected;
    }

    switchOnChange() {
        this.textboxCompany.visible = this.switchIntercompany.checked === true;
    }

    async retransmitOnClick() {
        const selectedInterchangeIds = this.selectedInterchangeIds;
        const count = selectedInterchangeIds.length;
        if (count > 0) {
            const beginningDialog = "This will mark ";
            const endingDialog = count === 1
                ? " record for resubmission. Do you wish to continue?"
                : " records for resubmission. Do you wish to continue?";
            await CommonDialogs.showYesNo(beginningDialog + count + endingDialog).then(async (clickedYes) => {
                if (clickedYes) {
                    const message = "Data successfully submitted for reprocessing.";
                    if (count > 100) {
                        Api.post("lme/datafusion/retransmit-log", { order_id_list: selectedInterchangeIds });
                        Toast.showSuccessToast(message);
                    } else {
                        this.buttonRetransmit.busy = true;
                        Api.post("lme/datafusion/retransmit-log", { order_id_list: selectedInterchangeIds }).then(result => {
                            if (result) {
                                const errors = result?.data[0].error_count;
                                Toast.showSuccessToast(message);
                                if (errors > 0) {
                                    const errorMessage = errors === 1
                                        ? errors + " record was processed with errors."
                                        : errors + " records were processed with errors.";
                                    Snackbar.showWarningSnackbar(errorMessage)
                                }
                            }
                            this.buttonRetransmit.busy = false;
                        });
                    }
                } else {
                    this.buttonRetransmit.busy = false;
                }
            });
        }
    }

    async bypassBatch() {
        const selectedRowsLogIds = this.selectedRowsLogIds;
        const count = selectedRowsLogIds.length;
        if (count > 0) {
            this.buttonBypassBatch.busy = true;
            const beginningDialog = "Are you sure you want to bypass ";
            const endingDialog = count === 1
                ? " batch?"
                : " batches?";
            await CommonDialogs.showYesNo(beginningDialog + count + endingDialog).then(async (clickedYes) => {
                if (clickedYes) {
                    Api.post("lme/datafusion/bypass-batch", { order_id_list: selectedRowsLogIds }).then(result => {
                        if (result) {
                            const list: any[] = result?.data[0].bypass_batch;
                            if (list.length > 0) {
                                list.forEach(value => {
                                    const index = this.tableTransmissions.data.findIndex(valueData => valueData.get("edi_log.id") === value?.id);
                                    if (index > -1) {
                                        const modelRow = this.tableTransmissions.data.find(valueData => valueData.get("edi_log.id") === value?.id);
                                        const tableRow = this.tableTransmissions.findTableRow(modelRow);
                                        modelRow.set("edi_log.was_successful", "V");
                                        modelRow.set("edi_log.descr", value?.descr);
                                        this.tableTransmissions.redisplaySingleRow(index, modelRow, false, true);
                                        const labelWasSuccessful = tableRow.findComponentById("labelWasSuccessful") as Label;
                                        this.setWasSuccessfulColor(labelWasSuccessful);
                                    }
                                });
                                const message = "Data successfully bypassed.";
                                Toast.showSuccessToast(message);
                            } else {
                                const message = "No records bypassed.";
                                Toast.showSuccessToast(message);
                            }
                        }
                    });
                    this.buttonBypassBatch.busy = false;
                } else {
                    this.buttonBypassBatch.busy = false;
                }
            });
        }
    }

    async addToRecovery() {
        const selectedRowsLogIds = this.selectedRowsLogIds;
        const count = selectedRowsLogIds.length;
        if (count > 0) {
            this.buttonAddRecovery.busy = true;
            const beginningDialog = "Are you sure you want to add ";
            const endingDialog = count === 1
                ? " record to recovery?"
                : " records to recovery?";
            await CommonDialogs.showYesNo(beginningDialog + count + endingDialog).then(async (clickedYes) => {
                if (clickedYes) {
                    Api.post("lme/datafusion/add-recovery", { order_id_list: selectedRowsLogIds }).then(result => {
                        if (result) {
                            const list: any[] = result?.data[0].add_recovery;
                            if (list?.length > 0) {
                                const newlyCreatedRecoveryIds = list.map(item => item.id).toString();
                                Navigation.navigateTo("lme/datafusion/RecoveryList?ids=" + newlyCreatedRecoveryIds, { newTab: true })
                            } else {
                                const message = count === 1
                                    ? "No record added to recovery. Recovery Entry already exists for selected Transmission Log."
                                    : "No records added to recovery. Recovery Entries already exist for selected Transmission Logs.";
                                Toast.showSuccessToast(message);
                            }
                        }
                        this.buttonAddRecovery.busy = false;
                    });
                } else {
                    this.buttonAddRecovery.busy = false;
                }
            });
        }
    }

    async viewRecovery() {
        const selectedRowsLogIds = this.selectedRowsLogIds;
        const count = selectedRowsLogIds.length;
        if (count > 0) {
            this.buttonViewRecovery.busy = true;
            Api.post("lme/datafusion/view-recovery", { edi_log_ids: selectedRowsLogIds }).then(result => {
                if (result) {
                    const list: any[] = result?.data[0].recovery_ids;
                    if (list?.length > 0) {
                        const recoveryIds = list.map(item => item.id).toString();
                        Navigation.navigateTo("lme/datafusion/RecoveryList?recoveryIds=" + recoveryIds, { newTab: true })
                    } else {
                        const message = "No recovery entries exist for selected transmission logs.";
                        Toast.showSuccessToast(message);
                    }
                }
                this.buttonViewRecovery.busy = false;
            });
        } else {
            this.buttonViewRecovery.busy = false;
        }
    }

    private get selectedInterchangeIds(): any[] {
        return this.tableTransmissions.selectedRows.map(tableRow => {
            return {
                id: tableRow.data.get("edi_interchange.id"),
                direction: tableRow.data.get("edi_interchange.direction")
            };
        });
    }

    private get selectedRowsLogIds(): any[] {
        return this.tableTransmissions.selectedRows.map(tableRow => {
            return {
                edi_log_id: tableRow.data.get("edi_log.id")
            };
        });
    }

    textSearch() {
        this.textboxTextSearch.visible = !this.textboxTextSearch.visible;
        if (!this.textboxTextSearch.visible) {
            this.textboxTextSearch.clear();
        }
    }


    tableOnRowExpand(event: TableRowExpansionEvent) {
        if ((event.target as TableRow).data != null) {
            const row = (event.target as TableRow).data;
            const interchangeId = row.get("edi_interchange.id");
            const logId = row.get("edi_log.id");
            const description = row.get("edi_log.descr");
            const expandComp = event.expandComponents[0] as TransmissionControlExpanded;
            expandComp.interchangeId = interchangeId;
            expandComp.logId = logId;
            expandComp.description = description;
            expandComp.initialize();
        }
    }

    get expanded(): boolean {
        return this._expanded;
    }

    set expanded(value: boolean) {
        this._expanded = value;
    }

    private getContextMenuItems(): Partial<ContextMenuLabelProps>[] {
        return ContextMenuItemCreator.fromButtons(
            { button: this.buttonRetransmit, text: "Resubmit"  },
            { button: this.buttonBypassBatch, text: "Bypass"},
            { button: this.buttonAddRecovery, text: "Add to Recovery" },
            { button: this.buttonViewRecovery, text: "View Recovery" }
        )
    }
}
